'use client'

import Link from 'next/link'
import Image from 'next/image'
import dynamic from 'next/dynamic'
import { useState, useRef, useCallback, useEffect, useMemo } from 'react'
import { motion } from '@/lib/framer-motion-shim'
import { useI18n } from '@/lib/i18n/context'
// Couleurs et icônes seules : `impact-scoring` importe project-scores.json
// (1 Mo). `impact-dimensions` ne porte aucune donnée (constat I-2).
import { DIMENSION_COLORS, DIMENSION_ICONS } from '@/data/impact-dimensions'
import { CarteEssence } from '@/components/CarteEssence'
// `cobenefices` importe project-scores.json ; `cobenefices-base` non.
import { getScoreLabel } from '@/data/cobenefices-base'
import type { LevierAgricole } from '@/data/leviers-agricoles'
import CTASection from '@/components/CTASection'
import LazyMount from '@/components/LazyMount'
import type { CompanyData } from './page'
import type { ProjetSoutenu } from './projets-entreprise'
import { Pictogram } from '@/components/Pictogram'
import VagueBanniere from '@/components/VagueBanniere'

const ImpactRadar = dynamic(() => import('@/components/ImpactRadar'), { ssr: false })
const MiniMap = dynamic(() => import('@/components/MiniMap'), { ssr: false })
const CompanyProjectsMap = dynamic(() => import('@/components/CompanyProjectsMap'), { ssr: false })

/* ═══════ CONSTANTS ═══════ */
const FORESTRY_TYPES = ['boisement', 'reboisement', 'belle_foret']
const AGRI_TYPES = ['grandes_cultures', 'carbon_agri', 'vergers', 'haies', 'reduction_intrants']

/* ═══════ HELPERS ═══════ */

/* ═══════ SUB-COMPONENTS ═══════ */

function Card({ children }: { children: React.ReactNode }) {
  return <div className="bg-white rounded-2xl p-6 border border-gray-100">{children}</div>
}

function SectionTitle({ icon, title }: { icon: React.ReactNode; title: string }) {
  return (
    <div className="flex items-center gap-2.5 mb-4">
      <div className="text-forest">{icon}</div>
      <h2 className="font-heading font-bold text-xl text-gray-900">{title}</h2>
    </div>
  )
}

function CarbonGauge({ label, value, max, color, unit, numFmt, decimales }: {
  label: string; value: number; max: number; color: string; unit: string; numFmt: Intl.NumberFormat; decimales?: number
}) {
  const pct = Math.min((value / max) * 100, 100)
  // Une surface en ha se lit avec ses décimales : arrondir à l'entier écraserait
  // les petites contributions (0,92 ha affichés « 1 ha »).
  const valeurAffichee = decimales ? +value.toFixed(decimales) : Math.round(value)
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-sm text-gray-600">{label}</span>
        <span className="font-heading font-bold text-lg" style={{ color }}>
          {numFmt.format(valeurAffichee)} <span className="text-xs font-normal text-gray-500">{unit}</span>
        </span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
    </div>
  )
}

function CoBeneficeIcon({ type, color, size = 20 }: { type: string; color: string; size?: number }) {
  const props = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: color, strokeWidth: 1.8, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const }
  switch (type) {
    case 'biodiversite':
      return <svg {...props}><path d="M12 3c-1.2 4-4 6-8 6 0 5 3.5 9.5 8 12 4.5-2.5 8-7 8-12-4 0-6.8-2-8-6z" /><path d="M12 9v12" /><path d="M8 14l4-3 4 3" /></svg>
    case 'eau':
      return <svg {...props}><path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" /></svg>
    case 'sol':
      return <svg {...props}><path d="M2 22h20" /><path d="M6 18c2-3 3-6 3-10" /><path d="M12 18c0-4 1.5-7 4-10" /><path d="M18 18c-1-2-1.5-5-1-8" /><circle cx="8" cy="6" r="1.5" fill={color} /><circle cx="15" cy="5" r="1.5" fill={color} /></svg>
    case 'paysage':
      return <svg {...props}><path d="M2 20L8.5 10 13 16l3.5-4L22 20z" /><circle cx="17" cy="6" r="2.5" /></svg>
    case 'social':
      return <svg {...props}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
    default:
      return <svg {...props}><path d="M11 20A7 7 0 0 1 9.8 6.9C15.5 4.9 17 3.5 19 1c1 2 2 4.5 1 8-1 3.5-1.5 5.5-4 8.5" /><path d="M2 21c0-3 1.85-5.36 5.08-6" /></svg>
  }
}


function LevierCard({ levier, isFr }: { levier: LevierAgricole; isFr: boolean }) {
  return (
    <div className="rounded-xl border border-gray-100 p-4 hover:shadow-sm transition-shadow">
      <div className="flex items-start gap-3">
        <span className="flex-shrink-0"><Pictogram name={levier.icon} className="w-7 h-7 text-forest" /></span>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <h4 className="font-heading font-bold text-gray-900 text-sm">{levier.nom}</h4>
            <span className="text-xs bg-forest/10 text-forest px-2 py-0.5 rounded-full font-medium flex-shrink-0">{levier.impactClimat}</span>
          </div>
          <p className="text-gray-600 text-xs leading-relaxed mt-2">{levier.description}</p>
        </div>
      </div>
    </div>
  )
}

interface TimelineStep { label: string; description: string; done: boolean }

function getTimeline(statut: string, isFr: boolean): TimelineStep[] {
  const isLabellise = statut.toLowerCase() === 'labellisé'
  const isCertifie = statut.toLowerCase() === 'certifié'
  return [
    { label: isFr ? 'Notification du projet' : 'Project notification', description: isFr ? 'Le porteur de projet soumet sa demande au ministère de la Transition Écologique.' : 'The project owner submits their application to the ministry.', done: true },
    { label: isFr ? 'Audit de terrain' : 'Field audit', description: isFr ? 'Un auditeur indépendant vérifie les données du projet sur site.' : 'An independent auditor verifies the project data on site.', done: true },
    { label: isFr ? 'Labellisation' : 'Labelling', description: isFr ? 'Le projet est validé et obtient le Label Bas-Carbone officiel.' : 'The project is validated and receives the official Low-Carbon Label.', done: isLabellise || isCertifie },
    { label: isFr ? 'Suivi & monitoring' : 'Monitoring', description: isFr ? 'Suivi régulier de l\'évolution du projet et des indicateurs carbone.' : 'Regular monitoring of project progress and carbon indicators.', done: isCertifie },
    { label: isFr ? 'Certification des crédits' : 'Credit certification', description: isFr ? 'Les réductions d\'émissions sont vérifiées et les crédits carbone émis.' : 'Emission reductions are verified and carbon credits are issued.', done: isCertifie },
  ]
}

function getODDExplanation(oddNumero: number, projType: string, surfaceStr: string, co2Str: string, essencesCount: number, commune: string, isFr: boolean): string {
  const isForestry = FORESTRY_TYPES.includes(projType)
  const isAgri = AGRI_TYPES.includes(projType)
  const explanations: Record<number, { fr: string; en: string }> = {
    2: { fr: isAgri ? `En ${surfaceStr} ha de pratiques agricoles durables${commune ? ` à ${commune}` : ''}, ce projet améliore la productivité des sols et renforce la résilience de l'exploitation.` : `Ce projet contribue à la sécurité alimentaire en protégeant les sols et l'eau.`, en: isAgri ? `With ${surfaceStr} ha of sustainable farming${commune ? ` in ${commune}` : ''}, this project improves soil productivity and farm resilience.` : `This project contributes to food security by protecting soils and water.` },
    6: { fr: `Le couvert végétal sur ${surfaceStr} ha filtre les eaux de ruissellement et favorise la recharge des nappes souterraines.`, en: `Vegetation cover on ${surfaceStr} ha filters runoff and promotes groundwater recharge.` },
    13: { fr: `Impact principal : ce projet séquestre ${co2Str} tonnes de CO₂, dans le cadre du Label Bas-Carbone. Chaque tonne retirée de l'atmosphère contribue à lutter contre le dérèglement climatique.`, en: `Primary impact: this project sequesters ${co2Str} tons of CO₂, under the Label Bas-Carbone framework.` },
    15: { fr: isForestry ? `Ce projet de ${surfaceStr} ha ${essencesCount > 1 ? `plante ${essencesCount} essences, recréant` : 'recrée'} un écosystème forestier complet : habitat pour la faune, corridors écologiques, biodiversité des sols.` : isAgri ? `En réintroduisant des pratiques favorables à la vie des sols, ce projet restaure la vie terrestre sur ${surfaceStr} ha.` : `Ce projet contribue à protéger la vie terrestre.`, en: isForestry ? `This ${surfaceStr} ha project ${essencesCount > 1 ? `plants ${essencesCount} species, recreating` : 'recreates'} a complete forest ecosystem.` : isAgri ? `By reintroducing soil-friendly practices, this project restores terrestrial life on ${surfaceStr} ha.` : `This project contributes to terrestrial life protection.` },
    12: { fr: isForestry ? `La gestion forestière durable de ${surfaceStr} ha garantit une production de bois responsable en remplacement de matériaux à forte empreinte carbone.` : isAgri ? `Ce projet transforme les pratiques agricoles sur ${surfaceStr} ha vers des modes de production plus responsables.` : `Ce projet s'inscrit dans une démarche de consommation et production responsables.`, en: isForestry ? `Sustainable forest management of ${surfaceStr} ha ensures responsible wood production.` : isAgri ? `This project transforms farming practices on ${surfaceStr} ha towards more responsible production.` : `This project contributes to responsible consumption and production.` },
  }
  const entry = explanations[oddNumero]
  return entry ? (isFr ? entry.fr : entry.en) : (isFr ? `Ce projet contribue à cet objectif par ses actions de séquestration carbone et de renforcement des écosystèmes locaux.` : `This project contributes to this goal through its carbon sequestration and local ecosystem strengthening.`)
}

/* ═══════ COMPONENT ═══════ */

interface CompanyDetailProps {
  company: CompanyData
  /**
   * Projets soutenus, déjà résolus contre le catalogue côté serveur : voir
   * `projets-entreprise.ts`. Le composant les résolvait lui-même, ce qui
   * embarquait les 423 projets bruts dans le paquet du navigateur et recopiait
   * le projet entier dans son état (constat I-2).
   */
  projets: ProjetSoutenu[]
}

export default function CompanyDetail({ company, projets }: CompanyDetailProps) {
  const { t, locale } = useI18n()
  const isFr = locale === 'fr'
  const p = (path: string) => (locale === 'en' ? `/en${path}` : path)
  const numFmt = new Intl.NumberFormat(isFr ? 'fr-FR' : 'en-US')

  const primaryColor = company.branding?.primaryColor || '#1A5632'
  const secondaryColor = company.branding?.secondaryColor || '#D4A24C'

  // Detect if primaryColor is "light" → use dark text in hero instead of white
  const isLightColor = useMemo(() => {
    const hex = primaryColor.replace('#', '')
    const r = parseInt(hex.substring(0, 2), 16)
    const g = parseInt(hex.substring(2, 4), 16)
    const b = parseInt(hex.substring(4, 6), 16)
    // Relative luminance (WCAG formula)
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
    return luminance > 0.55
  }, [primaryColor])

  // Hero text classes: white on dark backgrounds, dark on light backgrounds
  const heroText = isLightColor && !company.branding?.heroImage?.url ? 'text-gray-900' : 'text-white'
  const heroTextMuted = isLightColor && !company.branding?.heroImage?.url ? 'text-gray-600' : 'text-white/60'
  const heroTextSemi = isLightColor && !company.branding?.heroImage?.url ? 'text-gray-700' : 'text-white/80'
  const heroBadgeBg = isLightColor && !company.branding?.heroImage?.url ? 'bg-black/10' : 'bg-white/15'

  // Featured project first, then by year desc
  const sortedProjects = [...projets].sort((a, b) => {
    if (a.isFeatured && !b.isFeatured) return -1
    if (!a.isFeatured && b.isFeatured) return 1
    return (b.year || 0) - (a.year || 0)
  })

  const description = isFr ? company.description.fr : company.description.en
  const testimonial = isFr ? company.testimonial.fr : company.testimonial.en
  const sector = isFr ? company.sector.fr : company.sector.en

  // Aggregate metrics — use stored prorata values when available, fallback to percent-based calc
  const totalCO2 = sortedProjects.reduce((acc, pr) => {
    if (pr.contribution) return acc + pr.contribution
    if (pr.contributionPercent) return acc + (pr.co2ProrataCommercialisable ?? 0)
    return acc + pr.co2Total
  }, 0)
  const totalSurfaceM2 = sortedProjects.reduce((acc, pr) => {
    if (pr.contributionSurfaceM2) return acc + pr.contributionSurfaceM2
    const pct = pr.contributionPercent
    return acc + (pct ? Math.round(pr.surface * pct / 100 * 10000) : pr.surface * 10000)
  }, 0)
  const totalSurface = totalSurfaceM2 / 10000 // m² → ha pour affichage
  const totalTrees = sortedProjects.reduce((acc, pr) => {
    if (pr.contributionNbArbres != null) return acc + pr.contributionNbArbres
    const pct = pr.contributionPercent
    return acc + (pct ? Math.round(pr.nbArbres * pct / 100) : pr.nbArbres)
  }, 0)

  // ── Accordion state for multi-project pages ──
  const hasMultipleProjects = sortedProjects.length > 1
  const defaultOpenSlug = sortedProjects.find(p => p.isFeatured)?.slug ?? sortedProjects[0]?.slug ?? null
  const [openProjectSlug, setOpenProjectSlug] = useState<string | null>(defaultOpenSlug)
  const expandedRef = useRef<HTMLDivElement>(null)

  const toggleProject = useCallback((slug: string) => {
    setOpenProjectSlug(prev => {
      const next = prev === slug ? null : slug
      // auto-scroll after state update
      if (next) {
        setTimeout(() => {
          expandedRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }, 100)
      }
      return next
    })
  }, [])

  return (
    <>
      {/* ═══════ HERO ═══════ */}
      <section
        className="pt-32 pb-24 relative overflow-hidden"
        style={company.branding?.heroImage?.url ? undefined : { background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 50%, ${primaryColor}bb 100%)` }}
      >
        {company.branding?.heroImage?.url && (
          <>
            <Image src={company.branding.heroImage.url} alt={company.branding.heroImage.alt || company.name} fill className="object-cover" sizes="100vw" priority fetchPriority="high" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />
          </>
        )}
        <div className="container-site relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Link
              href={p('/entreprises')}
              className={`${heroTextMuted} text-sm hover:opacity-100 transition-colors inline-flex items-center gap-1 mb-8 block`}
            >
              {isFr ? '← Toutes les entreprises' : '← All companies'}
            </Link>

            <div className="flex flex-col md:flex-row items-start md:items-center gap-8">
              {/* Logo */}
              <div
                className="w-24 h-24 md:w-28 md:h-28 rounded-2xl bg-white flex items-center justify-center flex-shrink-0 shadow-lg overflow-hidden"
              >
                {company.logoMedia?.url ? (
                  <Image
                    src={company.logoMedia.url}
                    alt={company.logoMedia.alt || company.name}
                    width={company.logoMedia.width || 112}
                    height={company.logoMedia.height || 112}
                    className="w-full h-full object-contain p-3"
                  />
                ) : company.logoUrl ? (
                  <>
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={company.logoUrl}
                      alt={company.name}
                      className="w-full h-full object-contain p-3"
                      onError={(e) => {
                        const el = e.currentTarget
                        el.style.display = 'none'
                        if (el.nextElementSibling) (el.nextElementSibling as HTMLElement).style.display = 'flex'
                      }}
                    />
                    <span
                      className="hidden items-center justify-center w-full h-full font-heading font-bold text-2xl"
                      style={{ color: primaryColor }}
                    >
                      {company.initials}
                    </span>
                  </>
                ) : (
                  <span
                    className="flex items-center justify-center w-full h-full font-heading font-bold text-2xl"
                    style={{ color: primaryColor }}
                  >
                    {company.initials}
                  </span>
                )}
              </div>

              <div>
                <div className="flex items-center gap-3 mb-2">
                  <span className={`${heroBadgeBg} ${heroTextSemi} text-xs font-semibold px-3 py-1 rounded-full`}>
                    {sector}
                  </span>
                  <span className={`${heroBadgeBg} ${heroTextSemi} text-xs font-semibold px-3 py-1 rounded-full`}>
                    Label Bas-Carbone
                  </span>
                </div>
                <h1 className={`font-heading font-extrabold text-3xl md:text-4xl lg:text-5xl ${heroText} leading-tight`}>
                  {company.name}
                </h1>
                <p className={`mt-3 ${heroTextMuted} text-sm md:text-base max-w-xl`}>
                  {/* Formulation factuelle : ce gabarit s'applique à chaque fiche
                      entreprise, donc toute allégation de neutralité serait publiée
                      au nom d'un tiers nommé — ce que la loi encadre et ce que nous
                      refusons déjà à nos clients dans leur espace. */}
                  {isFr
                    ? `${company.name} soutient des projets de compensation carbone avec Stock CO2`
                    : `${company.name} supports carbon offset projects with Stock CO2`}
                </p>
              </div>
            </div>

            {/* Hero metrics */}
            {sortedProjects.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10">
                {[
                  { value: String(sortedProjects.length), label: isFr ? (sortedProjects.length > 1 ? 'projets soutenus' : 'projet soutenu') : (sortedProjects.length > 1 ? 'projects supported' : 'project supported') },
                  { value: numFmt.format(Math.round(totalCO2)), suffix: ' t', label: isFr ? 'tCO₂ contribuées' : 'tCO₂ contributed' },
                  ...(totalSurfaceM2 > 0 ? [{ value: numFmt.format(totalSurface), suffix: ' ha', label: isFr ? 'surface financée' : 'funded area' }] : []),
                  ...(totalTrees > 0 ? [{ value: numFmt.format(totalTrees), label: isFr ? 'arbres financés' : 'trees funded' }] : []),
                ].slice(0, 4).map((stat) => (
                  <div key={stat.label} className={`${heroBadgeBg} backdrop-blur-sm rounded-xl p-4 text-center border ${isLightColor && !company.branding?.heroImage?.url ? 'border-black/10' : 'border-white/10'}`}>
                    <p className={`font-heading font-extrabold text-2xl ${heroText}`}>
                      {stat.value}{stat.suffix || ''}
                    </p>
                    <p className={`text-xs ${heroTextMuted} mt-1`}>{stat.label}</p>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        </div>
        <VagueBanniere />
      </section>

      {/* ═══════ DESCRIPTION + TÉMOIGNAGE ═══════ */}
      <section className="section-padding bg-cream">
        <div className="container-site max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Description */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-3 bg-white rounded-2xl p-8 border border-gray-100"
            >
              <h2 className="font-heading font-bold text-xl text-gray-900 mb-4">
                {isFr ? 'L\'engagement' : 'The commitment'}
              </h2>
              <p className="text-gray-600 leading-relaxed">{description}</p>
            </motion.div>

            {/* Testimonial */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="lg:col-span-2 rounded-2xl p-8 flex flex-col justify-center border"
              style={{ backgroundColor: `${primaryColor}08`, borderColor: `${primaryColor}15` }}
            >
              <svg className="w-8 h-8 mb-4 opacity-20" style={{ color: primaryColor }} viewBox="0 0 24 24" fill="currentColor">
                <path d="M4.583 17.321C3.553 16.227 3 15 3 13.011c0-3.5 2.457-6.637 6.03-8.188l.893 1.378c-3.335 1.804-3.987 4.145-4.247 5.621.537-.278 1.24-.375 1.929-.311 1.804.167 3.226 1.648 3.226 3.489a3.5 3.5 0 01-3.5 3.5c-1.073 0-2.099-.49-2.748-1.179zm10 0C13.553 16.227 13 15 13 13.011c0-3.5 2.457-6.637 6.03-8.188l.893 1.378c-3.335 1.804-3.987 4.145-4.247 5.621.537-.278 1.24-.375 1.929-.311 1.804.167 3.226 1.648 3.226 3.489a3.5 3.5 0 01-3.5 3.5c-1.073 0-2.099-.49-2.748-1.179z" />
              </svg>
              {/* Citation d'un tiers, guillemets fermants compris + attribution
                  nommée : mêmes règles que TestimonialCard.tsx (page d'accueil). */}
              <blockquote className="text-gray-700 leading-relaxed italic text-sm">
                « {testimonial} »
              </blockquote>
              {company.testimonialAuthor && (
                <footer className="mt-4 pt-4 flex items-center gap-3" style={{ borderTopColor: `${primaryColor}15`, borderTopWidth: 1 }}>
                  {company.testimonialAvatar?.url && (
                    <Image
                      src={company.testimonialAvatar.url}
                      alt={company.testimonialAuthor}
                      width={40}
                      height={40}
                      className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                    />
                  )}
                  <div>
                    <cite className="font-heading font-bold text-sm text-gray-900 not-italic block">{company.testimonialAuthor}</cite>
                    {company.testimonialRole && (
                      <p className="text-xs text-gray-500">{isFr ? company.testimonialRole.fr : company.testimonialRole.en}{company.testimonialRole && ` — ${company.name}`}</p>
                    )}
                  </div>
                </footer>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══════ PROJETS SOUTENUS — CARTE + CARDS ═══════ */}
      {(() => {
        // Préparer les projets avec coordonnées pour la carte
        const mapProjects = sortedProjects
          .filter((proj) => proj.coords)
          .map((proj) => ({
            slug: proj.slug,
            title: proj.displayName || proj.title,
            commune: proj.commune,
            region: proj.region,
            lat: proj.coords!.lat,
            lon: proj.coords!.lon,
            type: proj.type,
            co2Total: proj.co2Total,
            surface: proj.surface,
          }))

        const hasMap = mapProjects.length > 0
        const pinColorsLocal = ['#1A5632', '#D4A24C', '#2563EB', '#DC2626', '#7C3AED', '#0891B2']

        // Trouver l'index d'un projet dans la liste (pour la couleur du pin)
        const projIndexMap = new Map(sortedProjects.map((p, i) => [p.slug, i]))

        return (
          <section className="py-12" style={{ background: 'linear-gradient(180deg, #f8faf8 0%, #ffffff 100%)' }}>
            <div className="container-site">
              {/* En-tête */}
              <div className="text-center mb-8">
                <span className="inline-block font-heading font-semibold text-xs uppercase tracking-widest px-4 py-1.5 rounded-full mb-3" style={{ backgroundColor: `${primaryColor}10`, color: primaryColor }}>
                  {isFr ? `${sortedProjects.length} projet${sortedProjects.length > 1 ? 's' : ''} soutenu${sortedProjects.length > 1 ? 's' : ''}` : `${sortedProjects.length} supported project${sortedProjects.length > 1 ? 's' : ''}`}
                </span>
                <h2 className="font-heading font-bold text-2xl text-gray-900 mb-2">
                  {isFr ? 'Nos projets sur le territoire' : 'Our projects across France'}
                </h2>
                <p className="text-gray-500 text-sm max-w-lg mx-auto">
                  {isFr
                    ? 'Des actions concrètes de séquestration carbone, ancrées dans les territoires français'
                    : 'Concrete carbon sequestration actions, rooted in French territories'}
                </p>
              </div>

              {/* Carte — pleine largeur, hauteur modérée */}
              {hasMap && (
                <div className="mb-8">
                  <LazyMount placeholderClassName="h-[340px] rounded-xl bg-gray-100 animate-pulse">
                    <CompanyProjectsMap
                      projects={mapProjects}
                      activeSlug={openProjectSlug}
                      onPinClick={(slug) => {
                        setOpenProjectSlug(slug)
                        setTimeout(() => {
                          expandedRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                        }, 200)
                      }}
                      primaryColor={primaryColor}
                      height="h-[340px]"
                    />
                  </LazyMount>
                </div>
              )}

              {/* Cards projets */}
              {hasMultipleProjects ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {sortedProjects.map((proj, i) => {
                    const coverImg = proj.coverImage
                    const isOpen = openProjectSlug === proj.slug
                    const pinColor = pinColorsLocal[i % pinColorsLocal.length]
                    const hasCoords = proj.coords != null

                    return (
                      <button
                        key={proj.slug}
                        onClick={() => toggleProject(proj.slug)}
                        onMouseEnter={() => { if (hasCoords) setOpenProjectSlug(proj.slug) }}
                        className={`group text-left rounded-2xl overflow-hidden border-2 transition-all duration-300 hover:shadow-lg ${isOpen ? 'shadow-md scale-[1.02]' : 'border-gray-100 hover:border-gray-200'}`}
                        style={isOpen ? { borderColor: pinColor } : undefined}
                      >
                        {/* Image cover avec badge pin */}
                        <div className="relative h-44 overflow-hidden">
                          {coverImg ? (
                            <Image src={coverImg.url} alt={coverImg.alt} fill className="object-cover transition-transform duration-500 group-hover:scale-105" sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw" />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200" />
                          )}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />

                          {/* Numéro de pin en overlay */}
                          {hasCoords && (
                            <div className="absolute top-3 left-3">
                              <span
                                className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold shadow-lg"
                                style={{ backgroundColor: pinColor }}
                              >
                                {i + 1}
                              </span>
                            </div>
                          )}

                          {/* Badges type + featured */}
                          <div className="absolute bottom-3 left-3 flex items-center gap-2">
                            <span className="bg-white/20 backdrop-blur-sm text-white text-xs font-semibold px-2.5 py-1 rounded-full capitalize">
                              {proj.type.replace(/_/g, ' ')}
                            </span>
                            {proj.isFeatured && (
                              <span className="bg-amber-400/30 backdrop-blur-sm text-amber-100 text-xs font-semibold px-2.5 py-1 rounded-full">
                                {isFr ? 'Principal' : 'Featured'}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Contenu */}
                        <div className="p-4 bg-white">
                          <h3 className="font-heading font-bold text-sm text-gray-900 mb-1 truncate">{proj.displayName || proj.title}</h3>
                          <p className="text-xs text-gray-500 mb-3 flex items-center gap-1">
                            <svg className="w-3 h-3 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                            {proj.commune ? `${proj.commune}, ` : ''}{proj.region}
                          </p>
                          <div className="grid grid-cols-3 gap-2 text-center border-t border-gray-100 pt-3">
                            <div>
                              <p className="font-heading font-bold text-sm" style={{ color: primaryColor }}>
                                {proj.contribution ? numFmt.format(proj.contribution) + ' t' : numFmt.format(proj.co2Total) + ' t'}
                              </p>
                              <p className="text-[10px] text-gray-400 mt-0.5">CO₂</p>
                            </div>
                            <div>
                              <p className="font-heading font-bold text-sm" style={{ color: primaryColor }}>
                                {proj.contributionSurfaceM2 ? numFmt.format(proj.contributionSurfaceM2 / 10000) + ' ha' : numFmt.format(proj.surface) + ' ha'}
                              </p>
                              <p className="text-[10px] text-gray-400 mt-0.5">{isFr ? 'surface' : 'area'}</p>
                            </div>
                            <div>
                              <p className="font-heading font-bold text-sm" style={{ color: primaryColor }}>
                                {(proj.contributionNbArbres ?? proj.nbArbres) > 0 ? numFmt.format(proj.contributionNbArbres ?? proj.nbArbres) : '—'}
                              </p>
                              <p className="text-[10px] text-gray-400 mt-0.5">{isFr ? 'plants prévus' : 'planned seedlings'}</p>
                            </div>
                          </div>
                          {isOpen && (
                            <div className="mt-3 text-center">
                              <span className="inline-flex items-center gap-1 text-xs font-medium" style={{ color: primaryColor }}>
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                                {isFr ? 'Détail ci-dessous' : 'Details below'}
                              </span>
                            </div>
                          )}
                        </div>
                      </button>
                    )
                  })}
                </div>
              ) : null}
            </div>
          </section>
        )
      })()}

      {/* Présentation complète du projet sélectionné (ou de tous si un seul) */}
      {sortedProjects.filter(proj => !hasMultipleProjects || proj.slug === openProjectSlug).map((proj, projIndex) => {
        // Tout ce qui vient des catalogues est calculé côté serveur.
        const { essCount, essencesList, essencesDetailed, impact, coverImage, odds, coBenefices, leviersForMethod } = proj
        const co2PerHa = proj.surface > 0 ? (proj.co2Total / proj.surface).toFixed(1) : '—'
        const density = proj.surface > 0 && proj.nbArbres > 0 ? Math.round(proj.nbArbres / proj.surface) : 0
        const typeLabel = proj.type.replace(/_/g, ' ')
        const isForestry = FORESTRY_TYPES.includes(proj.type)
        const isAgri = AGRI_TYPES.includes(proj.type)
        const timeline = getTimeline(proj.statut, isFr)
        const surfaceStr = numFmt.format(proj.surface)
        const co2Str = numFmt.format(Math.round(proj.co2Total))
        const diversityLevel = essencesList.length >= 5 ? (isFr ? 'forte' : 'strong') : essencesList.length >= 2 ? (isFr ? 'modérée' : 'moderate') : (isFr ? 'faible' : 'low')

        // ── Métriques proportionnelles à la contribution de l'entreprise ──
        const pct = proj.contributionPercent
        const hasContrib = !!(proj.contribution || (pct && pct > 0))
        const compCO2 = proj.contribution || (pct ? (proj.co2ProrataCommercialisable ?? 0) : proj.co2Total)
        const compSurfaceM2 = proj.contributionSurfaceM2 ?? (pct ? Math.round(proj.surface * pct / 100 * 10000) : proj.surface * 10000)
        const compSurface = +(compSurfaceM2 / 10000).toFixed(2) // m² → ha
        const compTrees = proj.contributionNbArbres ?? (pct ? Math.round(proj.nbArbres * pct / 100) : proj.nbArbres)

        // Labels de type enrichis
        const typeLabels: Record<string, { fr: string; en: string }> = {
          boisement: { fr: 'Boisement', en: 'Afforestation' },
          reboisement: { fr: 'Reboisement', en: 'Reforestation' },
          belle_foret: { fr: 'Conversion en futaie', en: 'High forest conversion' },
          grandes_cultures: { fr: 'Grandes cultures bas-carbone', en: 'Low-carbon crops' },
          carbon_agri: { fr: 'Carbon agri', en: 'Carbon agriculture' },
          vergers: { fr: 'Vergers agroforestiers', en: 'Agroforestry orchards' },
          reduction_intrants: { fr: 'Réduction d\'intrants', en: 'Input reduction' },
          batiment: { fr: 'Matériaux bois', en: 'Wood materials' },
          elevage: { fr: 'Élevage bas-carbone', en: 'Low-carbon livestock' },
        }
        const typeFr = typeLabels[proj.type]?.fr || typeLabel
        const typeEn = typeLabels[proj.type]?.en || typeLabel

        return (
          <div key={proj.slug} ref={proj.slug === openProjectSlug ? expandedRef : undefined}>
            {/* ── Project hero image ── */}
            {coverImage && (
              <section className="relative h-[320px] md:h-[420px] overflow-hidden">
                <Image src={coverImage.url} alt={coverImage.alt} fill className="object-cover" sizes="100vw" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                  <div className="container-site">
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <span className="bg-white/15 backdrop-blur-sm text-amber text-xs font-semibold px-3 py-1 rounded-full">{isFr ? typeFr : typeEn}</span>
                      <span className="bg-white/15 backdrop-blur-sm text-white/90 text-xs font-medium px-3 py-1 rounded-full">{proj.region}</span>
                      {proj.isFeatured && sortedProjects.length > 1 && (
                        <span className="bg-amber/30 text-amber text-xs font-semibold px-3 py-1 rounded-full">{isFr ? 'Projet principal' : 'Featured project'}</span>
                      )}
                    </div>
                    <h2 className="font-heading font-extrabold text-2xl md:text-4xl text-white drop-shadow-lg">{proj.displayName || proj.title}</h2>
                    {proj.commune && <p className="text-white/60 text-sm mt-1">{proj.commune}{proj.numeroDepartement ? ` ${proj.numeroDepartement}` : ''}{proj.departement ? `, ${proj.departement}` : ''}</p>}
                  </div>
                </div>
              </section>
            )}
            {!coverImage && (
              <section className="py-10 bg-white border-b border-gray-100">
                <div className="container-site text-center">
                  <span className="inline-block font-heading font-semibold text-xs uppercase tracking-widest px-4 py-1.5 rounded-full mb-4" style={{ backgroundColor: `${primaryColor}10`, color: primaryColor }}>
                    {projIndex === 0 ? (isFr ? 'Le projet soutenu' : 'The supported project') : (isFr ? 'Autre projet soutenu' : 'Another supported project')}
                  </span>
                  <h2 className="font-heading font-extrabold text-2xl md:text-3xl text-gray-900">{proj.displayName || proj.title}</h2>
                </div>
              </section>
            )}

            {/* ── Bandeau contribution entreprise ── */}
            {hasContrib && (
              <section className="py-6 border-b border-gray-100" style={{ backgroundColor: `${primaryColor}08` }}>
                <div className="container-site">
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: primaryColor }}>
                      {isFr ? `Contribution de ${company.name}` : `${company.name}'s contribution`}
                    </span>
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: primaryColor }}>{pct}%</span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                    <div>
                      <p className="font-heading font-extrabold text-2xl" style={{ color: primaryColor }}>{numFmt.format(compCO2)} t</p>
                      <p className="text-xs text-gray-500">CO₂ {isFr ? 'contribué' : 'contributed'}</p>
                    </div>
                    <div>
                      <p className="font-heading font-extrabold text-2xl" style={{ color: primaryColor }}>{numFmt.format(compSurface)} ha</p>
                      <p className="text-xs text-gray-500">{isFr ? 'surface financée' : 'funded area'}</p>
                    </div>
                    <div>
                      <p className="font-heading font-extrabold text-2xl" style={{ color: primaryColor }}>{compTrees > 0 ? numFmt.format(compTrees) : '—'}</p>
                      <p className="text-xs text-gray-500">{isFr ? 'arbres financés' : 'funded trees'}</p>
                    </div>
                    <div>
                      <p className="font-heading font-extrabold text-2xl" style={{ color: primaryColor }}>{proj.year || '—'}</p>
                      <p className="text-xs text-gray-500">{isFr ? 'année' : 'year'}</p>
                    </div>
                  </div>
                </div>
              </section>
            )}

            {/* ── Bandeau contribution (si pas déjà affiché via hasContrib) ou métriques projet simplifiées ── */}
            {!hasContrib && (
              <section className="bg-cream py-6 border-b border-gray-100">
                <div className="container-site">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                    <div><p className="font-heading font-extrabold text-xl text-forest">{surfaceStr} ha</p><p className="text-xs text-gray-500">{isFr ? 'surface' : 'area'}</p></div>
                    <div><p className="font-heading font-extrabold text-xl text-forest">{co2Str} t</p><p className="text-xs text-gray-500">CO₂</p></div>
                    <div><p className="font-heading font-extrabold text-xl text-forest">{proj.nbArbres > 0 ? numFmt.format(proj.nbArbres) : '—'}</p><p className="text-xs text-gray-500">{isFr ? 'plants prévus au dossier' : 'seedlings planned in the project file'}</p></div>
                    <div><p className="font-heading font-extrabold text-xl text-forest">{co2PerHa}</p><p className="text-xs text-gray-500">tCO₂/ha</p></div>
                  </div>
                </div>
              </section>
            )}

            {/* ── CONTENU COMPLET DU PROJET ── */}
            <section className="section-padding bg-cream">
              <div className="container-site">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  {/* ═══ LEFT COLUMN ═══ */}
                  <div className="lg:col-span-2 space-y-8">

                    {/* 1. Présentation enrichie */}
                    <Card>
                      <SectionTitle icon={<Pictogram name="clipboard" className="w-5 h-5 text-forest" />} title={isFr ? 'Présentation du projet' : 'Project presentation'} />
                      <div className="text-gray-600 leading-relaxed text-sm space-y-3">
                        <p>
                          {isFr
                            ? `${proj.displayName || proj.title} est un projet de ${typeFr.toLowerCase()} situé en ${proj.region}${proj.commune ? `, sur la commune de ${proj.commune}` : ''}${proj.departement ? ` (${proj.departement})` : ''}. ${['labellisé', 'certifié'].includes(proj.statut.toLowerCase()) ? 'Labellisé Label Bas-Carbone par le Ministère de la Transition Écologique' : 'Engagé dans le Label Bas-Carbone'} selon la méthode « ${proj.methode} », ce projet s'étend sur une surface de ${surfaceStr} hectares.`
                            : `${proj.displayName || proj.title} is a ${typeEn.toLowerCase()} project located in ${proj.region}${proj.commune ? `, municipality of ${proj.commune}` : ''}${proj.departement ? ` (${proj.departement})` : ''}. ${['labellisé', 'certifié'].includes(proj.statut.toLowerCase()) ? 'Labelled under the Label Bas-Carbone by the Ministry of Ecological Transition' : 'Engaged in the Label Bas-Carbone'} using the "${proj.methode}" method, this project covers ${surfaceStr} hectares.`}
                        </p>
                        <p>
                          {isFr
                            ? `Le potentiel de séquestration carbone est estimé à ${co2Str} tonnes de CO₂ sur la durée de vie du projet, soit une intensité de ${co2PerHa} tCO₂ par hectare.`
                            : `The carbon sequestration potential is estimated at ${co2Str} tonnes of CO₂ over the project's lifetime, an intensity of ${co2PerHa} tCO₂ per hectare.`}
                        </p>
                        {essencesList.length > 0 && proj.nbArbres > 0 && (
                          <p>{isFr
                            ? `Le dossier prévoit ${numFmt.format(proj.nbArbres)} plants de ${essencesList.length} essences différentes${density > 0 ? `, soit ${numFmt.format(density)} plants par hectare` : ''}. Cette diversité d'essences renforce la résilience de l'écosystème face aux aléas climatiques et favorise la biodiversité locale.`
                            : `The project file plans ${numFmt.format(proj.nbArbres)} seedlings of ${essencesList.length} different species${density > 0 ? `, i.e. ${numFmt.format(density)} seedlings per hectare` : ''}. This species diversity strengthens the ecosystem's resilience to climate risks and promotes local biodiversity.`}</p>
                        )}
                        {hasContrib && (
                          <p className="font-medium" style={{ color: primaryColor }}>
                            {isFr
                              ? `${company.name} finance ${pct ? `${pct}% de` : ''} ce projet, soit l'équivalent de ${numFmt.format(compCO2)} tonnes de CO₂${compTrees > 0 ? `, ${numFmt.format(compTrees)} plants prévus au dossier` : ''} et ${numFmt.format(compSurface)} ha.`
                              : `${company.name} funds ${pct ? `${pct}% of` : ''} this project, equivalent to ${numFmt.format(compCO2)} tonnes of CO₂${compTrees > 0 ? `, ${numFmt.format(compTrees)} seedlings planned in the project file` : ''} and ${numFmt.format(compSurface)} ha.`}
                          </p>
                        )}
                      </div>
                    </Card>

                    {/* 2. Impacts environnementaux & sociaux — détaillés */}
                    <Card>
                      <SectionTitle icon={<Pictogram name="leaf" className="w-5 h-5 text-forest" />} title={isFr ? 'Impacts environnementaux & sociaux' : 'Environmental & social impacts'} />
                      <p className="text-gray-500 text-sm mb-6">
                        {isFr
                          ? 'Au-delà de la séquestration carbone, chaque projet génère des impacts positifs mesurables sur l\'environnement et les communautés locales.'
                          : 'Beyond carbon sequestration, each project generates measurable positive impacts on the environment and local communities.'}
                      </p>
                      <div className="mb-6"><ImpactRadar result={impact} showLabels showLegend={false} /></div>
                      <div className="space-y-6 border-t border-gray-100 pt-6">
                        {[
                          {
                            key: 'climat', label: isFr ? 'Climat' : 'Climate', coKey: null as string | null,
                            explanation: isFr
                              ? `Ce projet séquestre ${co2PerHa} tCO₂/ha sur sa durée de vie. Le carbone est capté dans la biomasse et dans les sols. C'est l'impact principal : contribuer à l'atténuation du changement climatique.`
                              : `This project sequesters ${co2PerHa} tCO₂/ha over its lifetime. Carbon is captured in biomass and soils. This is the primary impact: contributing to climate change mitigation.`,
                          },
                          {
                            key: 'biodiversite', label: isFr ? 'Biodiversité' : 'Biodiversity', coKey: 'biodiversite',
                            explanation: isFr
                              ? `${essencesList.length > 0 ? `Avec ${essencesList.length} essences différentes (diversité ${diversityLevel}), ce projet crée` : 'Ce projet crée'} un habitat riche pour la faune et la flore. Les corridors écologiques favorisent la pollinisation et la lutte biologique.`
                              : `${essencesList.length > 0 ? `With ${essencesList.length} different species (${diversityLevel} diversity), this project creates` : 'This project creates'} a rich habitat for wildlife. Ecological corridors promote pollination and biological control.`,
                          },
                          {
                            key: 'eau', label: isFr ? 'Eau' : 'Water', coKey: 'eau',
                            explanation: isFr
                              ? `Sur ${surfaceStr} ha, le couvert végétal filtre les eaux, réduit le ruissellement et l'érosion, et favorise la recharge des nappes phréatiques.`
                              : `Over ${surfaceStr} ha, vegetation filters water, reduces runoff and erosion, and promotes groundwater recharge.`,
                          },
                          {
                            key: 'sols', label: isFr ? 'Sols' : 'Soils', coKey: 'sol',
                            explanation: isFr
                              ? `${density > 0 ? `Avec ${density} plants prévus par hectare, le` : 'Le'} système racinaire structure le sol. La litière enrichit la matière organique et augmente la rétention d'eau.`
                              : `${density > 0 ? `With ${density} seedlings planned per hectare, the` : 'The'} root system structures the soil. Litter enriches organic matter and increases water retention.`,
                          },
                          {
                            key: 'socioEco', label: isFr ? 'Socio-économique' : 'Socio-economic', coKey: 'social',
                            explanation: isFr
                              ? `Ce projet de ${surfaceStr} ha${proj.commune ? ` à ${proj.commune}` : ''} fait appel à du travail sur le terrain pour sa mise en œuvre et son entretien, et participe à la valorisation du territoire et à la transition écologique.`
                              : `This ${surfaceStr} ha project${proj.commune ? ` in ${proj.commune}` : ''} relies on field work for its implementation and maintenance, and contributes to territory enhancement and the ecological transition.`,
                          },
                        ].map(({ key, label, coKey, explanation }) => {
                          const impactScore = impact.scores[key as keyof typeof impact.scores] ?? 0
                          const color = DIMENSION_COLORS[key as keyof typeof DIMENSION_COLORS] ?? '#666'
                          const coScore = coKey ? coBenefices.find(c => c.cobenefice.key === coKey)?.score ?? 0 : 0
                          const coLabel = coScore > 0 ? getScoreLabel(coScore, locale) : null
                          return (
                            <div key={key} className="p-4 rounded-xl border border-gray-100">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2.5">
                                  {coKey ? <CoBeneficeIcon type={coKey} color={color} size={22} /> : <span style={{ color }}><Pictogram name={DIMENSION_ICONS[key as keyof typeof DIMENSION_ICONS]} className="w-[22px] h-[22px]" /></span>}
                                  <span className="font-semibold text-gray-900">{label}</span>
                                </div>
                                <div className="flex items-center gap-3">
                                  {coLabel && <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: `${color}15`, color }}>{coLabel}</span>}
                                  <span className="text-sm font-bold" style={{ color }}>{impactScore}%</span>
                                </div>
                              </div>
                              <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-3">
                                <div className="h-full rounded-full" style={{ width: `${impactScore}%`, backgroundColor: color }} />
                              </div>
                              <p className="text-sm text-gray-600 leading-relaxed">{explanation}</p>
                            </div>
                          )
                        })}
                      </div>
                      <div className="mt-4 pt-4 border-t border-gray-100 text-center">
                        <p className="text-xs text-gray-500">{isFr ? 'Score global' : 'Global score'}</p>
                        <p className="font-heading font-bold text-2xl text-forest">{impact.average}%</p>
                      </div>
                    </Card>

                    {/* 3. ODD — Objectifs de Développement Durable */}
                    {odds.length > 0 && (
                      <Card>
                        <SectionTitle icon={<Pictogram name="target" className="w-5 h-5 text-forest" />} title={isFr ? 'Objectifs de Développement Durable' : 'Sustainable Development Goals'} />
                        <p className="text-gray-500 text-sm mb-5">
                          {isFr
                            ? `Ce projet contribue directement à ${odds.length} ODD des Nations Unies.`
                            : `This project directly contributes to ${odds.length} UN SDGs.`}
                        </p>
                        <div className="space-y-4">
                          {odds.map((odd) => (
                            <div key={odd.numero} className="p-4 rounded-xl border border-gray-100">
                              <div className="flex items-start gap-3">
                                <div className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-heading font-bold text-base flex-shrink-0" style={{ backgroundColor: odd.couleur }}>{odd.numero}</div>
                                <div className="min-w-0 flex-1">
                                  <p className="font-semibold text-gray-900 leading-tight">{isFr ? odd.titre : odd.titreEn}</p>
                                  <p className="text-gray-500 text-xs mt-1">{isFr ? odd.description : odd.descriptionEn}</p>
                                </div>
                              </div>
                              <div className="mt-3 p-3 bg-sage/30 rounded-lg">
                                <p className="text-sm text-gray-700 leading-relaxed">
                                  <span className="font-semibold text-forest">{isFr ? 'Pourquoi ce projet :' : 'Why this project:'}</span>{' '}
                                  {getODDExplanation(odd.numero, proj.type, surfaceStr, co2Str, essencesList.length, proj.commune ?? '', isFr)}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </Card>
                    )}

                    {/* 4. Données carbone — contribution uniquement */}
                    <Card>
                      <SectionTitle icon={<Pictogram name="chart" className="w-5 h-5 text-forest" />} title={isFr ? 'Données carbone' : 'Carbon data'} />
                      <div className="space-y-4 mt-2">
                        <CarbonGauge label={isFr ? `CO₂ contribué par ${company.name}` : `CO₂ contributed by ${company.name}`} value={compCO2} max={proj.co2Total * 1.1} color={primaryColor} unit="tCO₂" numFmt={numFmt} />
                        {compSurfaceM2 > 0 && (
                          <CarbonGauge label={isFr ? 'Surface financée' : 'Funded area'} value={compSurface} max={proj.surface * 1.1} color="#1B4D3E" unit="ha" numFmt={numFmt} decimales={2} />
                        )}
                        {compTrees > 0 && (
                          <CarbonGauge label={isFr ? 'Plants financés' : 'Funded seedlings'} value={compTrees} max={proj.nbArbres * 1.1} color="#2ecc71" unit={isFr ? 'plants' : 'seedlings'} numFmt={numFmt} />
                        )}
                        {density > 0 && (
                          <div className="flex items-center justify-between text-sm pt-2 border-t border-gray-100">
                            <span className="text-gray-500">{isFr ? 'Densité prévue au dossier' : 'Density planned in the project file'}</span>
                            <span className="font-heading font-bold text-forest text-lg">{numFmt.format(density)} {isFr ? 'plants/ha' : 'seedlings/ha'}</span>
                          </div>
                        )}
                      </div>
                      <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500">
                        {isFr ? 'Méthode' : 'Method'}: {proj.methode} — {isFr ? 'Statut' : 'Status'}: {proj.statut}
                      </div>
                    </Card>

                    {/* 5. Localisation + mini-carte */}
                    <Card>
                      <SectionTitle icon={<Pictogram name="map-pin" className="w-5 h-5 text-forest" />} title={isFr ? 'Localisation' : 'Location'} />
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-5">
                        {proj.commune && <div><p className="text-gray-500 text-xs">{isFr ? 'Commune' : 'Municipality'}</p><p className="font-medium text-gray-900">{proj.commune}</p></div>}
                        {proj.codePostal && <div><p className="text-gray-500 text-xs">{isFr ? 'Code postal' : 'Postal code'}</p><p className="font-medium text-gray-900">{proj.codePostal}</p></div>}
                        {proj.departement && <div><p className="text-gray-500 text-xs">{isFr ? 'Département' : 'Department'}</p><p className="font-medium text-gray-900">{proj.departement} {proj.numeroDepartement ?? ''}</p></div>}
                        <div><p className="text-gray-500 text-xs">{isFr ? 'Région' : 'Region'}</p><p className="font-medium text-gray-900">{proj.region}</p></div>
                      </div>
                      {proj.coords && <MiniMap lat={proj.coords.lat} lon={proj.coords.lon} label={proj.title} zoom={9} height="h-72" />}
                    </Card>

                    {/* 6. Chronologie du projet */}
                    {timeline.length > 0 && (
                      <Card>
                        <SectionTitle icon={<Pictogram name="calendar" className="w-5 h-5 text-forest" />} title={isFr ? 'Chronologie du projet' : 'Project timeline'} />
                        <div className="relative pl-8 space-y-6 mt-2">
                          <div className="absolute left-3 top-2 bottom-2 w-0.5 bg-gradient-to-b from-forest via-forest/50 to-gray-200" />
                          {timeline.map((step, i) => (
                            <div key={i} className="relative">
                              <div className={`absolute -left-5 w-4 h-4 rounded-full border-2 border-white ${step.done ? 'bg-forest' : 'bg-gray-300'}`} style={{ boxShadow: step.done ? '0 0 0 3px rgba(27,77,62,0.15)' : 'none' }} />
                              <div>
                                <p className={`text-sm font-semibold ${step.done ? 'text-forest' : 'text-gray-500'}`}>{step.label}</p>
                                <p className="text-xs text-gray-500 mt-0.5">{step.description}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </Card>
                    )}

                    {/* 7. Méthode Label Bas-Carbone */}
                    <Card>
                      <SectionTitle icon={<Pictogram name="landmark" className="w-5 h-5 text-forest" />} title={`${isFr ? 'Méthode' : 'Method'} · ${proj.methode}`} />
                      <div className="p-4 bg-sage/20 rounded-xl mb-4">
                        <p className="text-sm text-gray-700 leading-relaxed">
                          {isFr
                            ? `Le Label Bas-Carbone est un cadre de certification officiel du ministère de la Transition Écologique. Il garantit que les réductions d'émissions sont réelles, mesurables, vérifiables et additionnelles.`
                            : `The Low-Carbon Label is an official certification framework from the French Ministry of Ecological Transition. It guarantees that emission reductions are real, measurable, verifiable and additional.`}
                        </p>
                      </div>
                      <div className="space-y-2 mb-4">
                        <p className="text-sm font-semibold text-gray-800">{isFr ? 'Comment ça fonctionne :' : 'How it works:'}</p>
                        {[
                          isFr ? '1. Le porteur de projet dépose un dossier décrivant le scénario de référence et le scénario projet.' : '1. The project owner submits a file describing the reference and project scenarios.',
                          isFr ? '2. La différence donne les réductions d\'émissions nettes en tonnes de CO₂ équivalent.' : '2. The difference gives net emission reductions in CO₂ equivalent.',
                          isFr ? '3. Un auditeur indépendant vérifie les données sur le terrain.' : '3. An independent auditor verifies the data in the field.',
                          isFr ? '4. Le ministère délivre le Label et les crédits carbone sont émis.' : '4. The ministry issues the Label and carbon credits are issued.',
                          isFr ? '5. Le projet est suivi avec des vérifications périodiques.' : '5. The project is monitored with periodic verifications.',
                        ].map((step, i) => (
                          <p key={i} className="text-sm text-gray-600 leading-relaxed pl-1">{step}</p>
                        ))}
                      </div>
                      <div className="pt-4 border-t border-gray-100">
                        <a href="https://label-bas-carbone.ecologie.gouv.fr" target="_blank" rel="noopener noreferrer" className="text-sm text-forest font-medium hover:underline">
                          {isFr ? 'Site officiel du Label Bas-Carbone →' : 'Official Low-Carbon Label website →'}
                        </a>
                      </div>
                    </Card>

                    {/* 8. Essences détaillées avec fiches (forestier) */}
                    {isForestry && essencesDetailed.length > 0 && (
                      <Card>
                        <SectionTitle icon={<Pictogram name="tree" className="w-5 h-5 text-forest" />} title={isFr ? `Essences plantées (${essencesDetailed.length})` : `Planted species (${essencesDetailed.length})`} />
                        <p className="text-gray-500 text-sm mb-5">
                          {isFr
                            ? 'La diversité des essences renforce la résilience de la forêt face aux maladies et au changement climatique.'
                            : 'Species diversity strengthens forest resilience against diseases and climate change.'}
                        </p>
                        <div className="space-y-4">
                          {essencesDetailed.map((e, i) => (
                            <CarteEssence key={`${e.name}-${i}`} essence={e} isFr={isFr} />
                          ))}
                        </div>
                        {density > 0 && (
                          <p className="text-xs text-gray-500 mt-4">{isFr ? `Densité prévue au dossier : ${numFmt.format(density)} plants/ha` : `Density planned in the project file: ${numFmt.format(density)} seedlings/ha`}</p>
                        )}
                      </Card>
                    )}

                    {/* 8b. Essences tags (non-forestier) */}
                    {!isForestry && essencesList.length > 0 && (
                      <Card>
                        <SectionTitle icon={<Pictogram name="leaf" className="w-5 h-5 text-forest" />} title={isFr ? `Essences (${essencesList.length})` : `Species (${essencesList.length})`} />
                        <div className="flex flex-wrap gap-2">
                          {essencesList.map((ess, i) => (
                            <span key={i} className="bg-sage/40 text-forest text-xs font-medium px-3 py-1.5 rounded-full">{ess}</span>
                          ))}
                        </div>
                      </Card>
                    )}

                    {/* 9. Leviers agricoles */}
                    {isAgri && leviersForMethod.length > 0 && (
                      <Card>
                        <SectionTitle icon={<Pictogram name="wrench" className="w-5 h-5 text-forest" />} title={isFr ? `Leviers agricoles — ${proj.methode}` : `Agricultural levers — ${proj.methode}`} />
                        <p className="text-gray-500 text-sm mb-5">
                          {isFr
                            ? 'Pratiques mises en place pour réduire les émissions et augmenter le stockage carbone.'
                            : 'Practices implemented to reduce emissions and increase carbon storage.'}
                        </p>
                        <div className="space-y-4">
                          {leviersForMethod.map((levier) => <LevierCard key={levier.slug} levier={levier} isFr={isFr} />)}
                        </div>
                      </Card>
                    )}
                  </div>

                  {/* ═══ RIGHT SIDEBAR ═══ */}
                  <div className="space-y-6">
                    {/* Fiche projet */}
                    <div className="bg-white rounded-2xl p-6 border border-gray-100 lg:sticky lg:top-24">
                      <h3 className="font-heading font-bold text-lg text-gray-900 mb-4">{isFr ? 'Fiche projet' : 'Project summary'}</h3>
                      <div className="space-y-3 text-sm">
                        <div className="flex items-center justify-between"><span className="text-gray-500">{isFr ? 'Type' : 'Type'}</span><span className="font-semibold text-gray-900">{isFr ? typeFr : typeEn}</span></div>
                        <div className="flex items-center justify-between"><span className="text-gray-500">{isFr ? 'Méthode' : 'Method'}</span><span className="font-semibold text-gray-900 text-right max-w-[55%] truncate">{proj.methode}</span></div>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-500">{isFr ? 'Statut' : 'Status'}</span>
                          <span className={`font-semibold px-2 py-0.5 rounded-full text-xs ${proj.statut.toLowerCase() === 'certifié' ? 'bg-green-50 text-green-700' : 'bg-amber/10 text-amber'}`}>{proj.statut}</span>
                        </div>
                        <div className="flex items-center justify-between"><span className="text-gray-500">{isFr ? 'Région' : 'Region'}</span><span className="font-semibold text-gray-900">{proj.region}</span></div>
                      </div>

                      {/* ODD mini badges */}
                      {odds.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-gray-100">
                          <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-2">ODD</p>
                          <div className="flex flex-wrap gap-1.5">
                            {odds.map((odd) => (
                              <span key={odd.numero} className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: odd.couleur }} title={isFr ? odd.titre : odd.titreEn}>{odd.numero}</span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Score global */}
                      <div className="mt-4 pt-4 border-t border-gray-100 text-center">
                        <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-2">{isFr ? 'Score global' : 'Global score'}</p>
                        <p className="text-3xl font-bold text-forest">{impact.average}%</p>
                      </div>

                      {/* Encart contribution */}
                      {hasContrib && (
                        <div className="mt-4 pt-4 border-t border-gray-100">
                          <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-2" style={{ color: primaryColor }}>{isFr ? `Part de ${company.name}` : `${company.name}'s share`}</p>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between"><span className="text-gray-500">{isFr ? 'Part' : 'Share'}</span><span className="font-heading font-bold" style={{ color: primaryColor }}>{pct}%</span></div>
                            <div className="flex justify-between"><span className="text-gray-500">CO₂</span><span className="font-heading font-bold" style={{ color: primaryColor }}>{numFmt.format(compCO2)} t</span></div>
                            <div className="flex justify-between"><span className="text-gray-500">{isFr ? 'Surface' : 'Area'}</span><span className="font-heading font-bold" style={{ color: primaryColor }}>{numFmt.format(compSurface)} ha</span></div>
                            {compTrees > 0 && <div className="flex justify-between"><span className="text-gray-500">{isFr ? 'Arbres' : 'Trees'}</span><span className="font-heading font-bold" style={{ color: primaryColor }}>{numFmt.format(compTrees)}</span></div>}
                          </div>
                          <div className="mt-3">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden"><div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: primaryColor }} /></div>
                              <span className="text-xs font-bold" style={{ color: primaryColor }}>{pct}%</span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* CTA */}
                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <Link href={p(`/projets/${proj.slug}`)} className="block text-center font-semibold text-sm py-3 rounded-xl border-2 transition-colors hover:opacity-80" style={{ borderColor: primaryColor, color: primaryColor }}>
                          {isFr ? 'Voir la fiche projet complète →' : 'View full project page →'}
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Separator (only in single-project mode) */}
            {!hasMultipleProjects && projIndex < sortedProjects.length - 1 && (
              <div className="bg-white py-6"><div className="container-site"><hr className="border-gray-200" /></div></div>
            )}
          </div>
        )
      })}

      {/* ═══════ POURQUOI STOCK CO2 ═══════ */}
      <section className="py-12 bg-cream">
        <div className="container-site max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="font-heading font-bold text-xl text-gray-900">
              {isFr ? 'Pourquoi Stock CO2 ?' : 'Why Stock CO2?'}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              {
                icon: 'lock',
                titleFr: 'Contribution sécurisée',
                titleEn: 'Secured contribution',
                descFr: 'La seule solution du marché à proposer fiducie, assurance et marges de sécurité.',
                descEn: 'The only solution offering trust, insurance and safety margins.',
              },
              {
                icon: 'landmark',
                titleFr: 'Label Bas-Carbone',
                titleEn: 'Label Bas-Carbone',
                descFr: 'Certification officielle de l\'État français, crédits inscrits au registre national.',
                descEn: 'Official French state certification, credits on the national registry.',
              },
              {
                icon: 'chart',
                titleFr: 'Suivi et rapports',
                titleEn: 'Monitoring & reports',
                descFr: 'Espace en ligne, rapports d\'impact personnalisés et certificat officiel.',
                descEn: 'Online dashboard, personalized impact reports and official certificate.',
              },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl p-5 border border-gray-100 text-center">
                <span className="block mb-2"><Pictogram name={item.icon} className="w-7 h-7 text-forest" /></span>
                <h3 className="font-heading font-bold text-sm text-gray-900 mb-1">{isFr ? item.titleFr : item.titleEn}</h3>
                <p className="text-xs text-gray-500">{isFr ? item.descFr : item.descEn}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ BADGE — page offerte ═══════ */}
      <section className="py-8 bg-white border-t border-gray-100">
        <div className="container-site text-center">
          <p className="text-xs text-gray-500">
            {isFr
              ? 'Cette page est offerte par Stock CO2 à ses partenaires pour valoriser leur engagement climatique.'
              : 'This page is offered by Stock CO2 to its partners to showcase their climate commitment.'}
          </p>
          <Link href={p('/entreprises')} className="text-xs font-medium text-forest hover:underline mt-1 inline-block">
            {isFr ? 'Votre entreprise aussi ? Contactez-nous →' : 'Your company too? Contact us →'}
          </Link>
        </div>
      </section>

      {/* ═══════ CTA ═══════ */}
      <CTASection
        badge={isFr ? 'Vous aussi ?' : 'You too?'}
        title={isFr ? 'Engagez votre entreprise avec Stock CO2' : 'Commit your company with Stock CO2'}
        subtitle={isFr
          ? 'Échangeons sur vos enjeux RSE et vos objectifs de contribution carbone.'
          : 'Let\'s discuss your CSR challenges and carbon contribution goals.'}
        primaryCTA={{ label: isFr ? 'Nous contacter' : 'Contact us', href: p('/contact') }}
        secondaryCTA={{ label: isFr ? 'Voir les projets' : 'View projects', href: p('/projets') }}
      />
    </>
  )
}
